<template>
  <div class="w-full bg-slate-50 min-h-screen p-4" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-slate-700">Gerenciar Frentistas</h1>
        <p class="text-sm text-slate-500">Visualize e gerencie todos os frentistas cadastrados</p>
      </div>

      <!-- LISTAGEM -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <!-- BARRA DE FERRAMENTAS DA TABELA -->
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <div class="flex items-center gap-3">
                <v-icon color="orange" small>mdi-account-group</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">FRENTISTAS CADASTRADOS</h2>
              </div>
              
              <div class="flex items-center gap-4 flex-1 justify-end">
                <button 
                  v-if="isAdmin"
                  @click="$router.push('/attendants-manager/create')" 
                  class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-6 py-2 transition-all w-fit max-max whitespace-nowrap"
                  style="width: auto !important; flex: none !important;"
                >
                  <v-icon color="white" small>mdi-plus</v-icon>
                  <span class="font-bold text-sm">NOVO</span>
                </button>
                
                <div class="relative w-80">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
                  </div>
                  <input 
                    v-model="search"
                    placeholder="PESQUISAR FRENTISTA..."
                    class="bg-white/10 border border-white/20 rounded-lg py-2 pl-10 pr-4 text-[11px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                  >
                </div>
              </div>
          </div>

          <div class="p-0">
              <v-data-table
                :headers="headers"
                :items="data"
                :search="search"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                @page-count="pageCount = $event"
                :loading="loader"
                loading-text="CARREGANDO..."
                class="custom-table"
                hide-default-footer
              >
                <template v-slot:item.attendant_name="{ item }">
                  <div class="flex items-center gap-3 py-2">
                    <v-avatar size="32" class="border border-slate-100 bg-slate-50">
                      <img :src="item.attendant_photo ? $store.state.urlImages + item.attendant_photo : require('@/assets/no-image.jpg')" alt="Foto">
                    </v-avatar>
                    <div>
                      <span class="text-sm font-bold text-slate-700 block uppercase">{{ item.attendant_name }}</span>
                    </div>
                  </div>
                </template>

                <template v-slot:item.attendant_cpf="{ item }">
                  <span class="text-xs text-slate-500 font-mono">{{ item.attendant_cpf }}</span>
                </template>

                <template v-slot:item.attendant_tel="{ item }">
                  <span class="text-xs text-slate-500">{{ item.attendant_tel }}</span>
                </template>

                <template v-slot:item.action="{ item }">
                  <div class="flex justify-end gap-2">
                    <button 
                      @click="$router.push(`/attendants-manager/edit/${item.attendant_id}`)"
                      class="p-2 hover:bg-orange-50 text-orange-600 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <v-icon small color="orange">mdi-pencil</v-icon>
                    </button>
                    <button 
                      @click="deleteItem(item.attendant_id)"
                      class="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                      title="Excluir"
                    >
                      <v-icon small color="red">mdi-delete</v-icon>
                    </button>
                  </div>
                </template>

                <template v-slot:no-data>
                  <div class="py-12 flex flex-col items-center justify-center text-slate-400">
                    <v-icon size="48" color="slate-200">mdi-database-off</v-icon>
                    <p class="mt-2 text-sm">Nenhum frentista encontrado</p>
                  </div>
                </template>
              </v-data-table>

              <!-- Custom Pagination Footer -->
              <div class="bg-slate-50 px-6 py-8 border-t border-slate-200 flex flex-col items-center justify-center gap-6 text-xs text-slate-500 font-medium w-full">
                  <span>Exibindo {{ paginationStart }} de {{ data.length }} registros</span>
                  
                  <div class="flex gap-1">
                      <button 
                        @click="page > 1 ? page-- : null" 
                        :disabled="page === 1"
                        class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Anterior
                      </button>
                      
                      <button 
                        v-for="p in visiblePages" 
                        :key="p"
                        @click="page = p"
                        :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                        class="px-3 py-1.5 border rounded-md transition-colors font-medium"
                      >
                        {{ p }}
                      </button>

                      <button 
                        @click="page < pageCount ? page++ : null" 
                        :disabled="page === pageCount"
                        class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Próximo
                      </button>
                  </div>
              </div>
          </div>
      </section>

      <!-- NOTIFICAÇÕES -->
      <v-snackbar
        v-model="snack.show"
        :color="snack.color"
        timeout="3000"
        rounded="lg"
        elevation="24"
      >
        <div class="flex items-center gap-3">
          <v-icon color="white" small>
            {{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          <span class="text-xs font-bold uppercase tracking-wider">{{ snack.message }}</span>
        </div>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      search: "",
      loader: true,
      showSearch: false,
      data: [],
      snack: {
        show: false,
        message: "",
        color: ""
      },
      headers: [
        { text: "Nome", value: "attendant_name" },
        { text: "CPF", value: "attendant_cpf" },
        { text: "Telefone", value: "attendant_tel" },
        { text: "E-mail", value: "attendant_mail" },
        { text: "Ações", align: "right", value: "action", sortable: false }
      ],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters.getUserData.group_id === 1;
    },
    paginationStart() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);

      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get("/attendants")
        .then(r => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch(e => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/attendants/${id}`)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.initialize();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    }
  }
};
</script>

<style scoped>
/* Centralização e Utilidades Copiadas de Fornecedores */
.bg-slate-50 { background-color: #f8fafc; }
.bg-slate-700 { background-color: #333333 !important; }
.border-slate-200 { border-color: #e2e8f0; }
.text-slate-500 { color: #64748b; }
.text-slate-700 { color: #334155; }

.flex { display: flex; }
.flex-col { flex-direction: column; }
.items-center { align-items: center; }
.justify-center { justify-content: center; }
.gap-1 { gap: 0.25rem; }
.gap-4 { gap: 1rem; }
.gap-6 { gap: 1.5rem; }

.px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
.py-1\.5 { padding-top: 0.375rem; padding-bottom: 0.375rem; }
.px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
.py-4 { padding-top: 1rem; padding-bottom: 1rem; }
.py-8 { padding-top: 2rem; padding-bottom: 2rem; }

.border { border-style: solid; border-width: 1px; }
.border-t { border-top-width: 1px; }
.rounded-md { border-radius: 0.375rem; }

.transition-colors { transition-property: background-color, border-color, color, fill, stroke; transition-duration: 150ms; }
.hover\:bg-slate-100:hover { background-color: #f1f5f9; }
.disabled\:opacity-50:disabled { opacity: 0.5; }
.disabled\:cursor-not-allowed:disabled { cursor: not-allowed; }

.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 0.75rem !important; 
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 0.875rem !important;
}
</style>